/**
 * Trip App API Client
 * Handles all communication with the HTTPS backend
 */

// Determine API base URL
function getApiBaseUrl() {
  // If explicitly set via Vite env var (build time), use it
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // In production (runtime detection)
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    // Check if window.ENV is set (can be injected via HTML)
    if (window.ENV?.API_URL) {
      return window.ENV.API_URL;
    }
    // Default production API URL
    return 'https://api.pansbro.com:8443/api';
  }

  // Development fallback
  return '/api';
}

const API_BASE_URL = getApiBaseUrl();

class APIError extends Error {
  constructor(message, statusCode, url, type = 'API_ERROR') {
    super(message);
    this.name = 'APIError';
    this.statusCode = statusCode;
    this.url = url;
    this.type = type;
  }
}

class TripPlannerAPI {
  constructor(baseURL = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  async request(method, path, body = null) {
    const url = `${this.baseURL}${path}`;
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for session management
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new APIError(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          url,
          'HTTP_ERROR'
        );
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return null;
      }

      return response.json();
    } catch (err) {
      // Network errors (can't reach server)
      if (err.name === 'TypeError' || err.message.includes('Failed to fetch')) {
        throw new APIError(
          'Cannot reach the server. Check your internet connection.',
          0,
          url,
          'NETWORK_ERROR'
        );
      }
      // Re-throw API errors
      if (err instanceof APIError) {
        throw err;
      }
      // Unknown errors
      throw new APIError(err.message, 0, url, 'UNKNOWN_ERROR');
    }
  }

  // Helper methods
  get(path) {
    return this.request('GET', path);
  }

  post(path, body) {
    return this.request('POST', path, body);
  }

  put(path, body) {
    return this.request('PUT', path, body);
  }

  delete(path) {
    return this.request('DELETE', path);
  }

  // ============================================================================
  // AUTHENTICATION
  // ============================================================================

  async register(name, password) {
    return this.post('/auth/register', { name, password });
  }

  async login(name, password) {
    return this.post('/auth/login', { name, password });
  }

  async logout() {
    return this.post('/auth/logout');
  }

  async claimAccount(userId, password) {
    return this.post('/auth/claim', { user_id: userId, password });
  }

  async getMe() {
    return this.get('/auth/me');
  }

  // ============================================================================
  // USERS
  // ============================================================================

  async createUser(name) {
    return this.post('/users', { name });
  }

  async getUser(userId) {
    return this.get(`/users/${userId}`);
  }

  async listAllUsers() {
    return this.get('/users');
  }

  async updateUser(userId, name) {
    return this.put(`/users/${userId}`, { name });
  }

  // ============================================================================
  // TRIPS
  // ============================================================================

  async createTrip(name, createdBy) {
    return this.post('/trips', { name, created_by: createdBy });
  }

  async getTrip(tripId) {
    return this.get(`/trips/${tripId}`);
  }

  async getTripByInviteCode(inviteCode) {
    return this.get(`/invite/${inviteCode}`);
  }

  async updateTrip(tripId, name, userId) {
    return this.put(`/trips/${tripId}?user_id=${userId}`, { name });
  }

  async deleteTrip(tripId, userId) {
    return this.delete(`/trips/${tripId}?user_id=${userId}`);
  }

  async listTripsForUser(userId) {
    return this.get(`/users/${userId}/trips`);
  }

  async regenerateInviteCode(tripId) {
    return this.post(`/trips/${tripId}/regenerate-code`);
  }

  async getBoardView(tripId) {
    return this.get(`/trips/${tripId}/board`);
  }

  // ============================================================================
  // TRIP MEMBERS
  // ============================================================================

  async joinTrip(tripId, userId) {
    return this.post(`/trips/${tripId}/join`, { user_id: userId });
  }

  async listTripMembers(tripId) {
    return this.get(`/trips/${tripId}/members`);
  }

  async setMemberDates(tripId, userId, arriveAt, departAt) {
    return this.put(`/trips/${tripId}/members/${userId}/dates`, {
      arrive_at: arriveAt,
      depart_at: departAt,
    });
  }

  async setMemberRole(tripId, userId, role) {
    return this.put(`/trips/${tripId}/members/${userId}/role`, { role });
  }

  async removeTripMember(tripId, userId, requestingUserId) {
    return this.delete(`/trips/${tripId}/members/${userId}?requesting_user_id=${requestingUserId}`);
  }

  // ============================================================================
  // SECTORS
  // ============================================================================

  async createSector(tripId, name, description = '') {
    return this.post(`/trips/${tripId}/sectors`, { name, description });
  }

  async listSectors(tripId) {
    return this.get(`/trips/${tripId}/sectors`);
  }

  async updateSector(sectorId, name, description) {
    return this.put(`/sectors/${sectorId}`, { name, description });
  }

  async reorderSector(sectorId, sortOrder) {
    return this.put(`/sectors/${sectorId}/reorder`, { sort_order: sortOrder });
  }

  async deleteSector(sectorId) {
    return this.delete(`/sectors/${sectorId}`);
  }

  // ============================================================================
  // STYLES
  // ============================================================================

  async createStyle(createdBy, name, colour, description = '') {
    return this.post('/styles', { created_by: createdBy, name, colour, description });
  }

  async listOwnStyles(userId) {
    return this.get(`/users/${userId}/styles`);
  }

  async listVisibleStylesForUser(userId) {
    return this.get(`/users/${userId}/styles/visible`);
  }

  async listVisibleStylesForTrip(tripId) {
    return this.get(`/trips/${tripId}/styles/visible`);
  }

  async updateStyle(styleId, createdBy, name, colour, description) {
    return this.put(`/styles/${styleId}`, { created_by: createdBy, name, colour, description });
  }

  async deleteStyle(styleId, createdBy) {
    return this.delete(`/styles/${styleId}`, { created_by: createdBy });
  }

  // ============================================================================
  // CLIMBS
  // ============================================================================

  async createClimb(sectorId, climbData) {
    return this.post(`/sectors/${sectorId}/climbs`, {
      name: climbData.name,
      grade: climbData.grade,
      grade_sort: climbData.gradeSort,
      notes: climbData.notes || '',
      video_url: climbData.videoUrl || '',
      created_by: climbData.createdBy,
    });
  }

  async getClimb(climbId) {
    return this.get(`/climbs/${climbId}`);
  }

  async listClimbsBySector(sectorId) {
    return this.get(`/sectors/${sectorId}/climbs`);
  }

  async listClimbsByTrip(tripId) {
    return this.get(`/trips/${tripId}/climbs`);
  }

  async updateClimb(climbId, climbData) {
    return this.put(`/climbs/${climbId}`, {
      name: climbData.name,
      grade: climbData.grade,
      grade_sort: climbData.gradeSort,
      notes: climbData.notes || '',
      video_url: climbData.videoUrl || '',
      sector_id: climbData.sectorId,
    });
  }

  async deleteClimb(climbId) {
    return this.delete(`/climbs/${climbId}`);
  }

  async setClimbStyles(climbId, styleIds) {
    return this.put(`/climbs/${climbId}/styles`, { style_ids: styleIds });
  }

  // ============================================================================
  // CLIMB VIDEOS
  // ============================================================================

  async addClimbVideo(climbId, url, title = 'Beta') {
    return this.post(`/climbs/${climbId}/videos`, { url, title });
  }

  async listClimbVideos(climbId) {
    return this.get(`/climbs/${climbId}/videos`);
  }

  async updateClimbVideo(videoId, url, title) {
    return this.put(`/videos/${videoId}`, { url, title });
  }

  async deleteClimbVideo(videoId) {
    return this.delete(`/videos/${videoId}`);
  }

  async reorderClimbVideo(videoId, sortOrder) {
    return this.put(`/videos/${videoId}/reorder`, { sort_order: sortOrder });
  }

  // ============================================================================
  // INTERESTS
  // ============================================================================
  // Priority levels (from domain model):
  //   1 = Would Try
  //   2 = Keen (default)
  //   3 = Project

  async createInterest(climbId, userId, priority = 2, suggestionId = null) {
    return this.post(`/climbs/${climbId}/interests`, {
      user_id: userId,
      priority,
      suggestion_id: suggestionId,
    });
  }

  async removeInterest(climbId, userId) {
    return this.delete(`/climbs/${climbId}/interests/${userId}`);
  }

  async listInterestsByClimb(climbId) {
    return this.get(`/climbs/${climbId}/interests`);
  }

  async listInterestsByUserForTrip(userId, tripId) {
    return this.get(`/users/${userId}/trips/${tripId}/interests`);
  }

  // ============================================================================
  // SUGGESTIONS
  // ============================================================================

  async createSuggestion(climbId, suggestedBy, suggestedFor, reason = '') {
    return this.post('/suggestions', {
      climb_id: climbId,
      suggested_by: suggestedBy,
      suggested_for: suggestedFor,
      reason,
    });
  }

  async acceptSuggestion(suggestionId) {
    return this.post(`/suggestions/${suggestionId}/accept`);
  }

  async dismissSuggestion(suggestionId) {
    return this.post(`/suggestions/${suggestionId}/dismiss`);
  }

  async listPendingSuggestionsForUser(userId) {
    return this.get(`/users/${userId}/suggestions`);
  }

  async listPendingSuggestionsForUserInTrip(userId, tripId) {
    return this.get(`/users/${userId}/trips/${tripId}/suggestions`);
  }

  async listSuggestionsByClimb(climbId) {
    return this.get(`/climbs/${climbId}/suggestions`);
  }

  // ============================================================================
  // VIDEO WATCHES
  // ============================================================================

  async markVideoWatched(climbId, userId) {
    return this.post(`/climbs/${climbId}/watch`, { user_id: userId });
  }

  async getWatchedClimbs(userId, tripId = null) {
    const query = tripId ? `?trip_id=${tripId}` : '';
    return this.get(`/users/${userId}/watched${query}`);
  }
}

// Create singleton instance
const api = new TripPlannerAPI();

export default api;
